import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { getLowStockAlerts, dismissAlert, checkLowStock, getEmails } from '../lib/api';
import { Warning, Envelope, Check, ArrowsClockwise } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const fetchData = async () => {
    try {
      const [alertsRes, emailsRes] = await Promise.all([
        getLowStockAlerts(),
        getEmails()
      ]);
      setAlerts(alertsRes.data);
      setEmails(emailsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleRunCheck = async () => {
    setChecking(true);
    try {
      await checkLowStock();
      await fetchData();
    } catch (e) {
      console.error(e);
    } finally {
      setChecking(false);
    }
  };

  const handleDismiss = async (id) => {
    await dismissAlert(id);
    fetchData();
  };

  return (
    <Layout>
      <div data-testid="alerts-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Alerts & Notifications</h1>
            <p className="text-muted-foreground">
              Stockouts and low-stock alerts &middot;{' '}
              Pings the Slack + email targets configured in{' '}
              <a href="/location-reports" className="text-brand underline font-bold">
                Location Reports &rarr; Cycle Count
              </a>{' '}
              <span className="text-xs">(MOCKED)</span>
            </p>
          </div>
          <Button onClick={handleRunCheck} disabled={checking} data-testid="run-alert-check-btn" className="bg-brand hover:bg-brand/90">
            <ArrowsClockwise size={18} className={`mr-2 ${checking ? 'animate-spin' : ''}`} />
            {checking ? 'Checking...' : 'Run Low-Stock Check'}
          </Button>
        </div>

        <Tabs defaultValue="alerts" className="bg-card border border-border rounded-lg">
          <TabsList className="w-full border-b border-border p-0 h-auto bg-transparent rounded-none">
            <TabsTrigger value="alerts" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">
              Active Alerts ({alerts.length})
              {alerts.some(a => a.alert_kind === 'stockout') && (
                <span className="ml-2 inline-block px-1.5 py-0.5 bg-red-600 text-white text-[10px] font-bold tracking-wider uppercase">
                  {alerts.filter(a => a.alert_kind === 'stockout').length} stockout{alerts.filter(a => a.alert_kind === 'stockout').length > 1 ? 's' : ''}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="emails" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">
              Email Log ({emails.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="alerts" className="p-0 m-0">
            <div className="overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th>SKU Code</th>
                    <th>Name</th>
                    <th>Current Qty</th>
                    <th>Threshold</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
                  ) : alerts.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-12">
                      <Warning size={40} className="mx-auto mb-2 text-slate-300" />
                      <p className="text-muted-foreground">No active low-stock alerts</p>
                    </td></tr>
                  ) : alerts.map(a => {
                    const isStockout = a.alert_kind === 'stockout';
                    return (
                    <tr key={a.id} className={isStockout ? 'bg-red-50 border-l-4 border-red-600' : 'bg-amber-50'}>
                      <td className="font-mono font-semibold">
                        {isStockout ? (
                          <span className="inline-flex items-center gap-1">
                            <span className="inline-block w-2 h-2 bg-red-600 rounded-full animate-pulse" />
                            {a.sku_code}
                          </span>
                        ) : a.sku_code}
                      </td>
                      <td>
                        {a.sku_name}
                        {isStockout && (
                          <span className="ml-2 inline-block px-1.5 py-0.5 bg-red-600 text-white text-[10px] font-bold tracking-wider uppercase">
                            STOCKOUT
                          </span>
                        )}
                      </td>
                      <td className={`font-mono font-bold ${isStockout ? 'text-red-700' : 'text-red-600'}`}>{a.current_quantity}</td>
                      <td className="font-mono">{a.threshold}</td>
                      <td className="text-sm text-muted-foreground">{new Date(a.created_at).toLocaleString()}</td>
                      <td>
                        <Button size="sm" variant="outline" onClick={() => handleDismiss(a.id)}>
                          <Check size={14} className="mr-1" /> Dismiss
                        </Button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="emails" className="p-0 m-0">
            <div className="overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th className="w-10"></th>
                    <th>To</th>
                    <th>Subject</th>
                    <th>Category</th>
                    <th>Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-12">
                      <Envelope size={40} className="mx-auto mb-2 text-slate-300" />
                      <p className="text-muted-foreground">No emails sent yet</p>
                    </td></tr>
                  ) : emails.map(e => (
                    <tr key={e.id}>
                      <td><Envelope size={16} className="text-brand" weight="fill" /></td>
                      <td className="text-sm">{e.to}</td>
                      <td className="font-medium text-sm">{e.subject}</td>
                      <td><span className="text-xs uppercase font-bold text-muted-foreground">{e.category}</span></td>
                      <td className="text-sm text-muted-foreground">{new Date(e.sent_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
